/**
 * Autoplay (spec 33 section 5.2): a walkthrough that "types" into the real
 * page, in the visitor's own demo tenant.
 *
 * Nothing here is a mock. Every keystroke is a real `input` event on the real
 * control, so Vue's bindings see exactly what they would see from a person,
 * and the request the form then makes is the request a person's form makes.
 * That is the whole point (Mark, 2026-10-06: state stays server-backed, never
 * a browser-only imitation).
 *
 * HOW A VALUE GETS INTO A FRAMEWORK'S INPUT. Assigning `el.value = 'x'` changes
 * what the box shows and tells nobody: Vue's `@input` / `v-model` listen for
 * the `input` event, selects for `change`. So each step sets the value through
 * the element prototype's own setter (the one a framework cannot shadow) and
 * then dispatches the event the component listens for, bubbling, exactly as
 * the browser would. Our events are `isTrusted === false`; the components here
 * never check that, and the host uses the difference on purpose: a TRUSTED
 * pointer or key event during autoplay is the visitor taking over.
 *
 * FOCUS STAYS WHERE THE VISITOR LEFT IT (on the card's Pause button). Vue
 * needs the events, not focus, and moving focus into a field as a side effect
 * of a demo would be a change of context nobody asked for (WCAG 3.2.2): a
 * keyboard user pressing Space to pause would type a space into the form.
 * The host draws a ring around the control being typed into instead.
 *
 * Every wait is abortable through an AbortSignal and every failure is an
 * AutoplayStopped with a reason, so the host can turn any of them into "carry
 * on yourself" instead of a stall. No DOM is touched once the signal aborts.
 *
 * No Vue, no router, no library. The host finds elements; this acts on them.
 */
import { waitForElement } from './waitFor';

export class AutoplayStopped extends Error {
  /** @param {'aborted'|'missing'|'disabled'|'busy'|'no-option'|'timeout'|'unknown-op'} reason */
  constructor(reason, detail = '') {
    super(`autoplay stopped: ${reason}${detail ? ` (${detail})` : ''}`);
    this.name = 'AutoplayStopped';
    this.reason = reason;
  }
}

/** A pause that the visitor's Pause (or leaving) cuts short. */
export function sleep(ms, signal) {
  return new Promise((resolve, reject) => {
    if (signal && signal.aborted) return reject(new AutoplayStopped('aborted'));
    let timer = null;
    const onAbort = () => {
      clearTimeout(timer);
      reject(new AutoplayStopped('aborted'));
    };
    timer = setTimeout(
      () => {
        if (signal) signal.removeEventListener('abort', onAbort);
        resolve();
      },
      Math.max(0, ms || 0)
    );
    if (signal) signal.addEventListener('abort', onAbort, { once: true });
  });
}

function check(signal) {
  if (signal && signal.aborted) throw new AutoplayStopped('aborted');
}

/** The form control a target names: the target itself, or the first one inside it. */
export function controlIn(el) {
  if (!el) return null;
  if (/^(INPUT|TEXTAREA|SELECT)$/.test(el.tagName)) return el;
  return el.querySelector('input, textarea, select');
}

/** The clickable thing a target names: itself if it is one, else the first inside it. */
export function clickableIn(el) {
  if (!el) return null;
  const selector = 'button, a[href], [role="button"], [role="menuitem"], [role="option"]';
  if (el.matches && el.matches(selector)) return el;
  return el.querySelector(selector) || el;
}

function prototypeOf(el) {
  if (el.tagName === 'TEXTAREA') return window.HTMLTextAreaElement.prototype;
  if (el.tagName === 'SELECT') return window.HTMLSelectElement.prototype;
  return window.HTMLInputElement.prototype;
}

/** Set a control's value the way the browser does, past any framework override. */
export function setNativeValue(el, value) {
  const descriptor = Object.getOwnPropertyDescriptor(prototypeOf(el), 'value');
  if (descriptor && descriptor.set) descriptor.set.call(el, value);
  else el.value = value;
}

function fireInput(el, data, inputType = 'insertText') {
  let event;
  try {
    event = new window.InputEvent('input', { bubbles: true, data, inputType });
  } catch (e) {
    event = new window.Event('input', { bubbles: true });
  }
  el.dispatchEvent(event);
}

function fireChange(el) {
  el.dispatchEvent(new window.Event('change', { bubbles: true }));
}

function usable(control) {
  if (!control) throw new AutoplayStopped('missing');
  if (control.disabled) throw new AutoplayStopped('disabled');
  // Vue sets `composing` between compositionstart and compositionend: the
  // visitor is mid-way through an IME character in this very box. Their
  // typing wins; the host reads this as a takeover.
  if (control.composing) throw new AutoplayStopped('busy');
  return control;
}

/**
 * The pause before one character. A space or punctuation takes a little longer,
 * which is what makes it read as somebody typing rather than a ticker.
 */
export function beatFor(char, cadenceMs) {
  if (!cadenceMs) return 0;
  return /[\s,.\-/]/.test(char) ? Math.round(cadenceMs * 1.8) : cadenceMs;
}

/**
 * Type `text` into the control, one character at a time at `cadenceMs`
 * (0: all at once, which is what reduced motion asks for). Replaces what was
 * there unless `append`.
 */
export async function typeInto(el, text, { cadenceMs = 0, signal = null, append = false } = {}) {
  const control = usable(controlIn(el));
  check(signal);
  const value = String(text == null ? '' : text);
  let current = append ? String(control.value || '') : '';
  if (!append && control.value) {
    setNativeValue(control, '');
    fireInput(control, null, 'deleteContentBackward');
  }
  if (!cadenceMs) {
    setNativeValue(control, current + value);
    fireInput(control, value);
  } else {
    for (const char of value) {
      // eslint-disable-next-line no-await-in-loop
      await sleep(beatFor(char, cadenceMs), signal);
      check(signal);
      current += char;
      setNativeValue(control, current);
      fireInput(control, char);
    }
  }
  fireChange(control);
  return control.value;
}

/**
 * A datetime-local, date or time input. Browsers accept these whole or not at
 * all, so the value goes in at once: there is no honest way to "type" a date
 * picker. `value` is the control's own format ('2026-10-06T14:00').
 */
export async function setValue(el, value, { signal = null } = {}) {
  const control = usable(controlIn(el));
  check(signal);
  setNativeValue(control, value);
  fireInput(control, value);
  fireChange(control);
  return control.value;
}

function normalise(text) {
  return String(text || '')
    .replace(/\s+/g, ' ')
    .trim()
    .toLowerCase();
}

/**
 * Choose an option of a native select by value, by visible label (exact, then
 * starts-with), or by position among the real options (placeholders, which are
 * disabled, are not counted).
 */
export async function chooseOption(el, { value, label, index } = {}, { signal = null } = {}) {
  const control = usable(controlIn(el));
  if (control.tagName !== 'SELECT') throw new AutoplayStopped('missing', 'not a select');
  check(signal);
  const options = Array.from(control.options).filter(o => !o.disabled);
  let option = null;
  if (value !== undefined && value !== null) {
    option = options.find(o => o.value === String(value)) || null;
  }
  if (!option && label) {
    const want = normalise(label);
    option =
      options.find(o => normalise(o.textContent) === want) ||
      options.find(o => normalise(o.textContent).startsWith(want)) ||
      null;
  }
  if (!option && Number.isInteger(index)) option = options[index] || null;
  if (!option) throw new AutoplayStopped('no-option', label || String(value));
  setNativeValue(control, option.value);
  option.selected = true;
  fireInput(control, null, 'insertReplacementText');
  fireChange(control);
  return option.textContent.trim();
}

/**
 * A search-as-you-type picker (LwCombobox): type the query, wait for the
 * listbox the input controls to offer a real result, and choose it the way a
 * mouse does (LwCombobox selects on `mousedown`, so the input never blurs).
 * `option` is a label to prefer; otherwise the first result. The "Add ... as
 * a new ..." row is never chosen: a walkthrough must not create records the
 * visitor did not ask for.
 */
export async function pickFrom(
  el,
  { query, option = null } = {},
  { cadenceMs = 0, signal = null, timeoutMs = 6000, retries = 2, retryMs = 1500 } = {}
) {
  const input = usable(controlIn(el));
  await typeInto(input, query, { cadenceMs, signal });
  const listId = input.getAttribute('aria-controls');
  const list = () => (listId ? document.getElementById(listId) : null);
  const realOptions = () => {
    const box = list();
    if (!box) return [];
    return Array.from(box.querySelectorAll('[role="option"]')).filter(
      li => !/--create\b/.test(li.className)
    );
  };
  // The picker says "Search failed" when its request does (a dropped
  // connection, a server restarting). A person would type again; so does
  // autoplay, a couple of times, before handing the step back.
  const failed = () => {
    const box = list();
    return box ? box.querySelector('[class*="status--danger"]') : null;
  };
  let found = null;
  for (let attempt = 0; attempt <= retries && !found; attempt += 1) {
    if (attempt > 0) {
      // eslint-disable-next-line no-await-in-loop
      await sleep(retryMs, signal);
      // eslint-disable-next-line no-await-in-loop
      await typeInto(input, query, { cadenceMs: 0, signal });
    }
    // eslint-disable-next-line no-await-in-loop
    const seen = await waitForElement(() => realOptions()[0] || failed() || null, {
      timeoutMs,
      signal,
    });
    check(signal);
    if (!seen) break;
    found = realOptions()[0] || null;
  }
  if (!found) throw new AutoplayStopped('timeout', `no results for "${query}"`);
  const choices = realOptions();
  const want = option ? normalise(option) : null;
  const choice = (want && choices.find(li => normalise(li.textContent).startsWith(want))) || found;
  choice.dispatchEvent(
    new window.MouseEvent('mousedown', { bubbles: true, cancelable: true, button: 0 })
  );
  return choice.textContent.trim();
}

/**
 * The control labelled `label` inside `el`: by its <label for>, or by its own
 * aria-label (the invoice's line inputs). Exact text first, then a prefix, so
 * "Type" finds the load's Type before a stop's, in page order. This is what
 * lets ONE data-tour on a form address every field in it, instead of an
 * attribute per control.
 */
export function fieldIn(el, label) {
  if (!el) return null;
  const want = normalise(label);
  const labels = Array.from(el.querySelectorAll('label'));
  const text = node => normalise(node.textContent).replace(/\s*\*$/, '');
  const byLabel = match => {
    const hit = labels.find(match);
    if (!hit) return null;
    const id = hit.getAttribute('for');
    return (id && el.querySelector(`[id="${id.replace(/"/g, '\\"')}"]`)) || controlIn(hit);
  };
  const exact = byLabel(l => text(l) === want);
  if (exact) return exact;
  const aria = Array.from(el.querySelectorAll('input, select, textarea')).find(
    c => normalise(c.getAttribute('aria-label')) === want
  );
  // Last, a label that ENDS with the words: a form whose labels carry a
  // per-template prefix ("Carrier name", "Shipper email") is addressed by
  // what is constant ("name", "email").
  return (
    aria ||
    byLabel(l => text(l).startsWith(want)) ||
    byLabel(l => text(l).endsWith(` ${want}`)) ||
    null
  );
}

const CLICKABLE = 'button, a[href], [role="button"], [role="menuitem"]';

/** The clickable inside `el` whose visible text or aria-label is `name`. */
export function namedClickable(el, name) {
  if (!el) return null;
  const want = normalise(name);
  const all = Array.from(el.querySelectorAll(CLICKABLE));
  if (el.matches && el.matches(CLICKABLE)) all.unshift(el);
  const label = node => normalise(node.getAttribute('aria-label') || node.textContent);
  return all.find(node => label(node) === want) || all.find(n => label(n).startsWith(want)) || null;
}

/**
 * Press a button: the target itself, the first clickable inside it, or, with
 * `name`, the one inside it labelled so ("Save" in a dialog, "Create load"
 * in a form). A name never falls back to some other button.
 */
export async function press(el, { name = null, signal = null, timeoutMs = 3000 } = {}) {
  // A named button may not be there yet: the driver dialog's Select buttons
  // appear once its availability read lands (2026-10-10). Wait for it the
  // way chooseMenuItem waits for a menu item, rather than stopping at once.
  const target = name
    ? namedClickable(el, name) ||
      (await waitForElement(() => namedClickable(el, name), { timeoutMs, signal }))
    : clickableIn(el);
  if (!target) throw new AutoplayStopped('missing', name || '');
  if (target.disabled || target.getAttribute('aria-disabled') === 'true') {
    throw new AutoplayStopped('disabled');
  }
  check(signal);
  target.click();
}

/**
 * Tick a checkbox (an invoice line's "Add ... to the invoice"), or with
 * `off`, clear it (a truck's Active, to retire it). A box already as asked is
 * left alone: this op means "make it so", not "toggle". click() is what a
 * person's click does to a checkbox, and it fires the input and change
 * events the component listens for.
 */
export async function tick(el, { signal = null, off = false } = {}) {
  const box = usable(
    // A radio button is chosen the same way (a work order's "Repaired").
    el && (el.type === 'checkbox' || el.type === 'radio')
      ? el
      : el && el.querySelector('input[type="checkbox"], input[type="radio"]')
  );
  if (signal && signal.aborted) throw new AutoplayStopped('aborted');
  if (Boolean(box.checked) === Boolean(off)) box.click();
  return box.checked;
}

/**
 * Choose an item from an action menu (LwMenu): open it from its trigger if it
 * is not open, wait for the item, press it. `el` is the target that holds the
 * menu (a row's actions cell).
 */
export async function chooseMenuItem(el, item, { signal = null, timeoutMs = 3000 } = {}) {
  const trigger = el.querySelector('[aria-haspopup="menu"]') || clickableIn(el);
  if (!trigger) throw new AutoplayStopped('missing', 'menu');
  check(signal);
  if (trigger.getAttribute('aria-expanded') !== 'true') trigger.click();
  const want = normalise(item);
  const found = await waitForElement(
    () =>
      Array.from(el.querySelectorAll('[role="menuitem"]')).find(
        node => normalise(node.textContent) === want
      ) || null,
    { timeoutMs, signal }
  );
  check(signal);
  if (!found) throw new AutoplayStopped('no-option', item);
  if (found.disabled) throw new AutoplayStopped('disabled', item);
  found.click();
  return item;
}

const UNFILLED = /\{[a-zA-Z]+(?::[+-]?\d+)?\}/;
const UNFILLED_FIELDS = [
  'target',
  'field',
  'text',
  'query',
  'option',
  'value',
  'label',
  'name',
  'item',
];

/**
 * Run a step's scripted actions in order.
 *
 * @param {Array<object>} ops  [{ op: 'type'|'set'|'choose'|'pick'|'press'|'menu'|'check'|'pause', target, ... }]
 * @param {object} options
 * @param {(target: string) => Promise<Element|null>} options.find resolves a target to an element
 * @param {number} [options.cadenceMs] per-character pause; 0 under reduced motion
 * @param {number} [options.gapMs] pause between actions, so each one can be seen
 * @param {AbortSignal} [options.signal]
 * @param {(op: object, el: Element|null, index: number) => void} [options.onOp] before each action
 */
export async function run(ops, { find, cadenceMs = 0, gapMs = 0, signal = null, onOp } = {}) {
  for (let i = 0; i < (ops || []).length; i += 1) {
    const op = ops[i];
    check(signal);
    // A value the walkthrough could not find in this fleet stays a {token}.
    // Typing "{pickupQuery}" into a prospect's form would be worse than
    // stopping: hand the step to the visitor instead.
    const unfilled = UNFILLED_FIELDS.map(k => op[k]).find(
      v => typeof v === 'string' && UNFILLED.test(v)
    );
    if (unfilled) throw new AutoplayStopped('missing', unfilled);
    if (op.op === 'pause') {
      // eslint-disable-next-line no-await-in-loop
      await sleep(cadenceMs ? op.ms || 0 : 0, signal);
      continue;
    }
    // eslint-disable-next-line no-await-in-loop
    const found = op.target ? await find(op.target) : null;
    check(signal);
    const el = found && op.field ? fieldIn(found, op.field) : found;
    if (!el) throw new AutoplayStopped('missing', op.field || op.target);
    if (onOp) onOp(op, el, i);
    if (i > 0 && gapMs) {
      // eslint-disable-next-line no-await-in-loop
      await sleep(gapMs, signal);
    }
    const opts = { cadenceMs, signal };
    /* eslint-disable no-await-in-loop */
    if (op.op === 'type') await typeInto(el, op.text, opts);
    else if (op.op === 'set') await setValue(el, op.value, opts);
    else if (op.op === 'choose') await chooseOption(el, op, opts);
    else if (op.op === 'pick') await pickFrom(el, op, opts);
    else if (op.op === 'press') await press(el, { ...opts, name: op.name || null });
    else if (op.op === 'menu') await chooseMenuItem(el, op.item, opts);
    else if (op.op === 'check') await tick(el, { ...opts, off: Boolean(op.off) });
    else throw new AutoplayStopped('unknown-op', op.op);
    /* eslint-enable no-await-in-loop */
  }
}
